'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { readBlockedSlots, reserveBookingSlot, type BlockedSlot } from '@/lib/bookingStorage';
import { reportConversion } from '@/lib/conversions';
import { saveBooking } from '@/lib/bookings';
import { Stamp } from '@/components/instrument';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Calendar,
  Clock,
  Globe,
  Briefcase,
  MessageSquare,
  User,
  Mail,
  Building2,
  CalendarCheck,
} from 'lucide-react';

const consultationTypes = [
  {
    id: 'ctype-strategy',
    title: 'Business Strategy Session',
    duration: '60 min',
    description:
      'A comprehensive review of your business model, growth challenges, and strategic priorities. Best for founders and business owners.',
    best: 'Founders, SMB owners',
  },
  {
    id: 'ctype-marketing',
    title: 'Marketing Audit & Plan',
    duration: '45 min',
    description:
      'Deep dive into your current marketing performance, channels, and spend. Walk away with a prioritised action plan.',
    best: 'Marketing managers, growth leads',
  },
  {
    id: 'ctype-discovery',
    title: 'Service Discovery Call',
    duration: '30 min',
    description:
      'A focused conversation to understand your needs and determine which Noventi Labs services are the best fit.',
    best: 'Anyone exploring options',
  },
  {
    id: 'ctype-seo',
    title: 'SEO & Content Review',
    duration: '45 min',
    description:
      'Technical SEO audit combined with a content strategy review. Identify quick wins and long-term organic growth opportunities.',
    best: 'E-commerce, service businesses',
  },
];

const ALL_TIME_SLOTS = [
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
];

const BOOKING_TIMEZONE = 'Europe/Vilnius';
const BOOKING_WINDOW_DAYS = 180;
const timezones = [BOOKING_TIMEZONE];

interface BookingFormData {
  name: string;
  email: string;
  company: string;
  industry: string;
  stage: string;
  challenge: string;
  message: string;
  timezone: string;
}

function generateCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);
  return days;
}

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const stepLabels = ['Consultation Type', 'Date & Time', 'Your Details', 'Confirm'];

export default function BookingFlow() {
  const initialDate = new Date();
  const [step, setStep] = useState(0);
  const [consultationType, setConsultationType] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [calYear, setCalYear] = useState(initialDate.getFullYear());
  const [calMonth, setCalMonth] = useState(initialDate.getMonth());
  const [confirmed, setConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [blockedSlots, setBlockedSlots] = useState<BlockedSlot[]>([]);

  const form = useForm<BookingFormData>({
    defaultValues: { timezone: BOOKING_TIMEZONE },
  });

  // Load blocked slots from localStorage (set by admin panel)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('noventi_blocked_slots');
      if (stored) setBlockedSlots(readBlockedSlots(localStorage));
    } catch {
      // ignore
    }
  }, []);

  const calDays = generateCalendarDays(calYear, calMonth);
  const today = new Date();
  const todayDay = today.getDate();
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();
  const maximumDate = new Date(todayYear, todayMonth, todayDay + BOOKING_WINDOW_DAYS);
  const earliestMonth = new Date(todayYear, todayMonth, 1);
  const latestMonth = new Date(maximumDate.getFullYear(), maximumDate.getMonth(), 1);
  const currentMonth = new Date(calYear, calMonth, 1);

  const isDisabledDay = (day: number) => {
    const d = new Date(calYear, calMonth, day);
    return (
      d < new Date(todayYear, todayMonth, todayDay) ||
      d > maximumDate ||
      d.getDay() === 0 ||
      d.getDay() === 6
    );
  };

  const changeMonth = (offset: number) => {
    const next = new Date(calYear, calMonth + offset, 1);
    if (next < earliestMonth || next > latestMonth) return;
    setCalYear(next.getFullYear());
    setCalMonth(next.getMonth());
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const getDateKey = (day: number) => {
    const mm = String(calMonth + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${calYear}-${mm}-${dd}`;
  };

  const isTimeBlocked = (time: string) => {
    if (!selectedDate) return false;
    const dateKey = getDateKey(selectedDate);
    return blockedSlots.some((s) => s.date === dateKey && s.time === time);
  };

  const availableTimeSlots = ALL_TIME_SLOTS;

  const selectedType = consultationTypes.find((t) => t.id === consultationType);

  // The booking notification is the only thing that reaches the Noventi Labs
  // inbox, so a failure here is reported rather than swallowed — the visitor
  // keeps a confirmed booking either way, but is told to email instead.
  const sendAdminNotification = async (data: BookingFormData) => {
    if (!selectedType || !selectedDate || !selectedTime) return;
    setEmailStatus('sending');
    const payload = {
      ...data,
      consultationType: selectedType.title,
      duration: selectedType.duration,
      date: `${monthNames[calMonth]} ${selectedDate}, ${calYear}`,
      time: selectedTime,
    };

    try {
      const response = await fetch('/api/notify-booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setEmailStatus('sent');
        await recordBooking(data, true);
        return;
      }

      const detail = await response.text().catch(() => '');
      console.error(
        `Booking notification failed (HTTP ${response.status}):`,
        detail || '(no response body)'
      );
      setEmailStatus('failed');
      // Still record it. A booking the admin cannot see is worse than one whose
      // email did not arrive, and the stored row says which happened.
      await recordBooking(data, false);
    } catch (error) {
      console.error('Booking notification request failed:', error);
      setEmailStatus('failed');
      await recordBooking(data, false);
    }
  };

  /** Writes the booking where the admin dashboard can read it. Deliberately
   *  cannot fail the flow: the slot is taken and the email has been attempted,
   *  so a storage problem is logged, not shown. */
  const recordBooking = async (data: BookingFormData, emailDelivered: boolean) => {
    if (!selectedType || !selectedDate || !selectedTime) return;
    await saveBooking({
      name: data.name,
      email: data.email,
      company: data.company,
      industry: data.industry,
      stage: data.stage,
      challenge: data.challenge,
      message: data.message,
      consultationType: selectedType.title,
      duration: selectedType.duration,
      date: `${monthNames[calMonth]} ${selectedDate}, ${calYear}`,
      time: selectedTime,
      timezone: data.timezone,
      emailDelivered,
    });
  };

  const handleSubmit = async (data: BookingFormData) => {
    if (!consultationType || !selectedDate || !selectedTime) return;
    setIsSubmitting(true);
    setSubmitError('');

    // Reserving the slot is a browser-local convenience and can fail outright
    // when storage is blocked (private windows, strict privacy settings). Only
    // a genuine double booking may stop the submission — a storage failure must
    // never cost Noventi Labs the notification email.
    try {
      const dateKey = getDateKey(selectedDate);
      const reservation = reserveBookingSlot(localStorage, {
        date: dateKey,
        time: selectedTime,
      });
      if (!reservation.reserved) {
        setBlockedSlots(reservation.slots);
        setSelectedTime(null);
        setStep(1);
        setSubmitError('That time has just been taken. Please choose another available time.');
        setIsSubmitting(false);
        return;
      }
      setBlockedSlots(reservation.slots);
    } catch (error) {
      console.warn('Could not reserve the slot in this browser:', error);
    }

    try {
      // Store the confirmed booking for the existing admin notifications panel.
      // A damaged notification history must not undo a successfully reserved slot.
      try {
        const storedNotifications = JSON.parse(
          localStorage.getItem('noventi_admin_notifications') || '[]'
        );
        const existing = Array.isArray(storedNotifications) ? storedNotifications : [];
        const notification = {
          id: `notif-${Date.now()}`,
          type: 'confirmed_booking',
          title: 'Booking Confirmed',
          message: `${data.name} from ${data.company} confirmed a ${selectedType?.title}`,
          details: {
            name: data.name,
            email: data.email,
            company: data.company,
            industry: data.industry,
            stage: data.stage,
            challenge: data.challenge,
            consultationType: selectedType?.title,
            duration: selectedType?.duration,
            date: `${monthNames[calMonth]} ${selectedDate}, ${calYear}`,
            time: selectedTime,
            timezone: data.timezone,
            message: data.message,
          },
          read: false,
          createdAt: new Date().toISOString(),
        };
        existing.unshift(notification);
        localStorage.setItem('noventi_admin_notifications', JSON.stringify(existing.slice(0, 50)));
      } catch {
        // The slot itself is already reserved; notification history is secondary.
      }
      setConfirmed(true);
      /* The moment worth reporting: a slot is reserved and the visitor has been
         told so. Reported after the state change rather than before, so a tag
         that misbehaves cannot stop somebody seeing their own confirmation, and
         it carries the fact of the booking and nothing about who made it. Goes
         nowhere at all unless the visitor agreed to that purpose — the tags do
         not exist otherwise. */
      reportConversion('booking');
      await sendAdminNotification(data);
    } catch (error) {
      console.error('Booking submission failed:', error);
      setSubmitError('The booking could not be completed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 16px is not a style choice: iOS Safari zooms the whole page in when a
  // focused input's text is smaller than that, and the visitor then has to
  // pinch back out to see the rest of the form. 48px height keeps the field a
  // comfortable thumb target.
  const inputStyle: React.CSSProperties = {
    width: '100%',
    minHeight: 48,
    padding: '12px 14px',
    border: '1px solid rgba(var(--m-ink-rgb),0.15)',
    background: 'var(--m-bg)',
    fontSize: 16,
    color: 'var(--m-ink)',
    fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontFamily: 'monospace',
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    color: 'var(--m-ink-6)',
    marginBottom: 6,
  };

  // ─── CONFIRMATION SCREEN ────────────────────────────────────────────────────
  if (confirmed) {
    const formValues = form.getValues();
    return (
      <div className="min-h-screen m-ground">
        {/* Dark confirmation header */}
        <div
          style={{
            background: 'var(--m-band)',
            borderBottom: '1px solid var(--m-band-edge)',
            paddingTop: 80,
            paddingBottom: 48,
          }}
        >
          <div className="max-w-3xl mx-auto px-6 text-center">
            <div
              className="flex items-center justify-center mx-auto mb-6"
              style={{
                width: 56,
                height: 56,
                border: '1px solid rgba(var(--m-on-band-rgb),0.15)',
                background: 'rgba(var(--m-on-band-rgb),0.05)',
              }}
            >
              <CalendarCheck className="w-6 h-6" style={{ color: 'var(--m-on-band)' }} />
            </div>
            <p
              className="font-mono text-[10px] uppercase tracking-widest mb-3"
              style={{ color: 'rgba(var(--m-on-band-rgb),0.4)' }}
            >
              Booking Confirmed
            </p>
            <h1
              className="font-bold uppercase mb-4"
              style={{
                fontFamily: "'Archivo Narrow', sans-serif",
                fontSize: 'clamp(24px, 4vw, 40px)',
                color: 'var(--m-on-band)',
                letterSpacing: '0.03em',
                lineHeight: 1.1,
              }}
            >
              You&apos;re All Set
            </h1>
            <p
              style={{
                fontSize: 14,
                color: 'rgba(var(--m-on-band-rgb),0.5)',
                fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                lineHeight: 1.65,
              }}
            >
              Your booking is confirmed. This date and time is now unavailable for other bookings
              unless it is reopened in the admin panel.
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-12 space-y-8">
          {/* Appointment details card */}
          <div style={{ border: '1px solid rgba(var(--m-ink-rgb),0.1)', padding: 24 }}>
            {/* The slot, stamped. Date, time and reference are all real values
                the visitor just chose — the one thing that licenses this mark. */}
            <Stamp
              className="mb-5"
              lines={[
                'Appointment details',
                `${monthNames[calMonth]} ${selectedDate}, ${calYear} · ${selectedTime}`,
                `${selectedType?.duration ?? ''} · ${BOOKING_TIMEZONE}`,
              ]}
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              {[
                {
                  label: 'Consultation',
                  value: selectedType?.title ?? '—',
                  icon: <Briefcase className="w-3 h-3" />,
                },
                {
                  label: 'Duration',
                  value: selectedType?.duration ?? '—',
                  icon: <Clock className="w-3 h-3" />,
                },
                {
                  label: 'Date',
                  value: `${monthNames[calMonth]} ${selectedDate}, ${calYear}`,
                  icon: <Calendar className="w-3 h-3" />,
                },
                { label: 'Time', value: selectedTime ?? '—', icon: <Clock className="w-3 h-3" /> },
              ].map((item) => (
                <div key={item.label}>
                  <div
                    className="flex items-center gap-1.5 mb-1.5"
                    style={{ color: 'var(--m-muted-3)' }}
                  >
                    {item.icon}
                    <p className="font-mono text-[9px] uppercase tracking-widest">{item.label}</p>
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'var(--m-ink)',
                      fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
            <div
              className="mt-5 pt-5"
              style={{ borderTop: '1px solid rgba(var(--m-ink-rgb),0.08)' }}
            >
              <div
                className="flex items-center gap-1.5 mb-1.5"
                style={{ color: 'var(--m-muted-3)' }}
              >
                <Globe className="w-3 h-3" />
                <p className="font-mono text-[9px] uppercase tracking-widest">Timezone</p>
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--m-ink)',
                  fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                  fontWeight: 600,
                }}
              >
                {formValues.timezone}
              </p>
            </div>
          </div>

          {/* Reservation notice */}
          <div
            style={{
              border: '1px solid rgba(var(--m-ink-rgb),0.1)',
              padding: 20,
              background: 'rgba(var(--m-ink-rgb),0.02)',
            }}
          >
            <p
              className="font-mono text-[9px] uppercase tracking-widest mb-3"
              style={{ color: 'var(--m-muted-3)' }}
            >
              Time Reserved
            </p>
            <div
              style={{
                padding: '12px 16px',
                background: emailStatus === 'failed' ? 'var(--m-surface-2)' : 'var(--m-surface-2)',
                border:
                  emailStatus === 'failed'
                    ? '1px solid rgba(180,80,20,0.25)'
                    : '1px solid rgba(var(--m-ink-rgb),0.08)',
              }}
            >
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--m-ink-4)',
                  fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                  lineHeight: 1.65,
                  margin: 0,
                }}
              >
                {emailStatus === 'failed' ? (
                  <>
                    Your time has been reserved, but we could not deliver your details to our inbox
                    automatically. Please email{' '}
                    <strong style={{ color: 'var(--m-ink)' }}>info@noventilabs.com</strong> so we
                    can confirm your session and send the meeting link.
                  </>
                ) : (
                  <>
                    Your time has been reserved. You will receive an email confirmation with the
                    meeting link within the next 24 hours. If you want to change it, contact us at{' '}
                    <strong style={{ color: 'var(--m-ink)' }}>info@noventilabs.com</strong>.
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Next steps */}
          <div>
            <p
              className="font-mono text-[10px] uppercase tracking-widest mb-5"
              style={{ color: 'var(--m-muted)' }}
            >
              Next Steps
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  id: 'ns-1',
                  step: '01',
                  title: 'Booking saved',
                  description:
                    'Your selected date and time are confirmed and no longer available to book.',
                },
                {
                  id: 'ns-2',
                  step: '02',
                  title: 'Prepare your brief',
                  description:
                    'Think about your top 3 business challenges and any data or metrics you can share. The more context, the better.',
                },
                {
                  id: 'ns-3',
                  step: '03',
                  title: 'Join the call',
                  description:
                    'Noventi Labs will use the booking details you provided to prepare for the consultation.',
                },
                {
                  id: 'ns-4',
                  step: '04',
                  title: 'Receive your action plan',
                  description:
                    "Within 24 hours of your consultation, we'll send a personalised summary and recommended next steps.",
                },
              ].map((ns) => (
                <div
                  key={ns.id}
                  className="p-5"
                  style={{
                    border: '1px solid rgba(var(--m-ink-rgb),0.08)',
                    background: 'var(--m-surface-2)',
                  }}
                >
                  <p
                    className="font-mono text-[9px] uppercase tracking-widest mb-2"
                    style={{ color: 'var(--m-muted-4)' }}
                  >
                    Step {ns.step}
                  </p>
                  <p
                    className="font-semibold mb-2"
                    style={{
                      fontSize: 13,
                      color: 'var(--m-ink)',
                      fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    {ns.title}
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      color: 'var(--m-ink-6)',
                      fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                      lineHeight: 1.6,
                    }}
                  >
                    {ns.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <p
            className="text-center font-mono text-[9px] uppercase tracking-widest"
            style={{ color: 'var(--m-muted-5)' }}
          >
            Questions? Email us at{' '}
            <a
              href="mailto:info@noventilabs.com"
              style={{ color: 'var(--m-muted)', textDecoration: 'underline' }}
            >
              info@noventilabs.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  // ─── BOOKING FLOW ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen m-ground">
      {/* Dark header band */}
      <div
        style={{
          background: 'var(--m-band)',
          borderBottom: '1px solid var(--m-band-edge)',
          paddingTop: 80,
          paddingBottom: 40,
        }}
      >
        <div className="max-w-3xl mx-auto px-6">
          <p
            className="font-mono text-[10px] uppercase tracking-widest mb-3"
            style={{ color: 'rgba(var(--m-on-band-rgb),0.4)' }}
          >
            Book a Consultation
          </p>
          <h1
            className="font-bold uppercase"
            style={{
              fontFamily: "'Archivo Narrow', sans-serif",
              fontSize: 'clamp(22px, 3.5vw, 36px)',
              color: 'var(--m-on-band)',
              letterSpacing: '0.03em',
              lineHeight: 1.1,
            }}
          >
            Schedule Your Free Consultation
          </h1>
          <p
            style={{
              fontSize: 13,
              color: 'rgba(var(--m-on-band-rgb),0.45)',
              fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
              marginTop: 10,
            }}
          >
            No obligation. Just a focused conversation about your business and where we can help.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Step indicator */}
        <div className="flex items-center gap-0 mb-10">
          {stepLabels.map((label, i) => (
            <React.Fragment key={`step-ind-${label}`}>
              <div className="flex items-center gap-2">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 24,
                    height: 24,
                    border: `1px solid ${i <= step ? 'var(--m-ink)' : 'rgba(var(--m-ink-rgb),0.2)'}`,
                    background: i < step ? 'var(--m-ink)' : 'transparent',
                  }}
                >
                  {i < step ? (
                    <CheckCircle2 className="w-3 h-3" style={{ color: 'var(--m-bg)' }} />
                  ) : (
                    <span
                      className="font-mono text-[9px]"
                      style={{ color: i === step ? 'var(--m-ink)' : 'var(--m-muted-4)' }}
                    >
                      {i + 1}
                    </span>
                  )}
                </div>
                <span
                  className="font-mono text-[9px] uppercase tracking-widest hidden sm:block"
                  style={{ color: i === step ? 'var(--m-ink)' : 'var(--m-muted-4)' }}
                >
                  {label}
                </span>
              </div>
              {i < stepLabels.length - 1 && (
                <div
                  className="flex-1 mx-3"
                  style={{
                    height: 1,
                    background: i < step ? 'var(--m-ink)' : 'rgba(var(--m-ink-rgb),0.1)',
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 0: Consultation Type */}
        {step === 0 && (
          <div>
            <p
              className="font-mono text-[10px] uppercase tracking-widest mb-2"
              style={{ color: 'var(--m-muted)' }}
            >
              Step 01
            </p>
            <h2
              className="font-bold uppercase mb-6"
              style={{
                fontFamily: "'Archivo Narrow', sans-serif",
                fontSize: 20,
                color: 'var(--m-ink)',
                letterSpacing: '0.03em',
              }}
            >
              What type of consultation do you need?
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {consultationTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setConsultationType(type.id)}
                  className="text-left p-5 transition-all"
                  style={{
                    border: `1px solid ${consultationType === type.id ? 'var(--m-ink)' : 'rgba(var(--m-ink-rgb),0.12)'}`,
                    background:
                      consultationType === type.id ? 'rgba(var(--m-ink-rgb),0.03)' : 'var(--m-bg)',
                    outline: 'none',
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="font-mono text-[9px] uppercase tracking-widest"
                      style={{ color: 'var(--m-muted)' }}
                    >
                      {type.duration}
                    </span>
                    {consultationType === type.id && (
                      <CheckCircle2 className="w-3.5 h-3.5" style={{ color: 'var(--m-ink)' }} />
                    )}
                  </div>
                  <p
                    className="font-semibold mb-2"
                    style={{
                      fontSize: 15,
                      color: 'var(--m-ink)',
                      fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    {type.title}
                  </p>
                  <p
                    style={{
                      fontSize: 14,
                      color: 'var(--m-ink-6)',
                      fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                      lineHeight: 1.55,
                      marginBottom: 8,
                    }}
                  >
                    {type.description}
                  </p>
                  <p
                    className="font-mono text-[10px] uppercase tracking-widest"
                    style={{ color: 'var(--m-muted-3)' }}
                  >
                    Best for: {type.best}
                  </p>
                </button>
              ))}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setStep(1)}
                disabled={!consultationType}
                className="inline-flex items-center justify-center gap-2 font-mono uppercase text-[10px] tracking-widest px-6 min-h-[48px] w-full sm:w-auto transition-opacity"
                style={{
                  background: 'var(--m-ink)',
                  color: 'var(--m-bg)',
                  opacity: consultationType ? 1 : 0.3,
                  cursor: consultationType ? 'pointer' : 'not-allowed',
                  border: 'none',
                }}
              >
                Choose Date & Time <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Date & Time */}
        {step === 1 && (
          <div>
            <p
              className="font-mono text-[10px] uppercase tracking-widest mb-2"
              style={{ color: 'var(--m-muted)' }}
            >
              Step 02
            </p>
            <h2
              className="font-bold uppercase mb-6"
              style={{
                fontFamily: "'Archivo Narrow', sans-serif",
                fontSize: 20,
                color: 'var(--m-ink)',
                letterSpacing: '0.03em',
              }}
            >
              Select a date and time
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Calendar */}
              <div style={{ border: '1px solid rgba(var(--m-ink-rgb),0.1)', padding: 20 }}>
                <div className="flex items-center justify-between mb-4">
                  <p
                    className="font-mono text-[10px] uppercase tracking-widest"
                    style={{ color: 'var(--m-ink)' }}
                  >
                    {monthNames[calMonth]} {calYear}
                  </p>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      aria-label="Previous month"
                      onClick={() => changeMonth(-1)}
                      disabled={currentMonth <= earliestMonth}
                      className="flex items-center justify-center transition-opacity hover:opacity-60"
                      style={{
                        width: 28,
                        height: 28,
                        border: '1px solid rgba(var(--m-ink-rgb),0.15)',
                        background: 'transparent',
                        opacity: currentMonth <= earliestMonth ? 0.25 : 1,
                        cursor: currentMonth <= earliestMonth ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <ChevronLeft className="w-3.5 h-3.5" style={{ color: 'var(--m-ink-5)' }} />
                    </button>
                    <button
                      type="button"
                      aria-label="Next month"
                      onClick={() => changeMonth(1)}
                      disabled={currentMonth >= latestMonth}
                      className="flex items-center justify-center transition-opacity hover:opacity-60"
                      style={{
                        width: 28,
                        height: 28,
                        border: '1px solid rgba(var(--m-ink-rgb),0.15)',
                        background: 'transparent',
                        opacity: currentMonth >= latestMonth ? 0.25 : 1,
                        cursor: currentMonth >= latestMonth ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--m-ink-5)' }} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                    <div
                      key={`cal-hdr-${d}`}
                      className="text-center font-mono"
                      style={{
                        fontSize: 9,
                        color: 'var(--m-muted-3)',
                        padding: '4px 0',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}
                    >
                      {d}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {calDays.map((day, i) => (
                    <div key={`cal-day-${i}`}>
                      {day === null ? (
                        <div />
                      ) : (
                        <button
                          onClick={() => {
                            if (isDisabledDay(day)) return;
                            setSelectedDate(day);
                            setSelectedTime(null);
                            setSubmitError('');
                          }}
                          disabled={isDisabledDay(day)}
                          className="w-full transition-all"
                          style={{
                            aspectRatio: '1',
                            fontSize: 11,
                            fontFamily: 'monospace',
                            background: selectedDate === day ? 'var(--m-ink)' : 'transparent',
                            color:
                              selectedDate === day
                                ? 'var(--m-bg)'
                                : isDisabledDay(day)
                                  ? 'var(--m-muted-5)'
                                  : 'var(--m-ink-3)',
                            border: 'none',
                            cursor: isDisabledDay(day) ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {day}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Time slots */}
              <div style={{ border: '1px solid rgba(var(--m-ink-rgb),0.1)', padding: 20 }}>
                <p
                  className="font-mono text-[10px] uppercase tracking-widest mb-1"
                  style={{ color: 'var(--m-ink)' }}
                >
                  Available Times
                </p>
                {!selectedDate ? (
                  <p
                    style={{
                      fontSize: 11,
                      color: 'var(--m-muted-3)',
                      fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                      marginBottom: 16,
                    }}
                  >
                    Select a date first.
                  </p>
                ) : (
                  <p
                    style={{
                      fontSize: 11,
                      color: 'var(--m-muted)',
                      fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                      marginBottom: 16,
                    }}
                  >
                    {monthNames[calMonth]} {selectedDate}, {calYear}
                  </p>
                )}

                <div className="grid grid-cols-3 gap-1.5 mb-5">
                  {availableTimeSlots.map((time) => {
                    const blocked = isTimeBlocked(time);
                    return (
                      <button
                        key={`time-${time}`}
                        disabled={!selectedDate || blocked}
                        onClick={() => !blocked && setSelectedTime(time)}
                        title={blocked ? 'This time is unavailable' : undefined}
                        className="transition-all relative"
                        style={{
                          padding: '8px 4px',
                          fontSize: 11,
                          fontFamily: 'monospace',
                          border: `1px solid ${selectedTime === time ? 'var(--m-ink)' : blocked ? 'rgba(var(--m-ink-rgb),0.06)' : 'rgba(var(--m-ink-rgb),0.12)'}`,
                          background:
                            selectedTime === time
                              ? 'var(--m-ink)'
                              : blocked
                                ? 'rgba(var(--m-ink-rgb),0.03)'
                                : 'transparent',
                          color:
                            selectedTime === time
                              ? 'var(--m-bg)'
                              : blocked
                                ? 'var(--m-muted-5)'
                                : !selectedDate
                                  ? 'var(--m-muted-5)'
                                  : 'var(--m-ink-4)',
                          cursor: !selectedDate || blocked ? 'not-allowed' : 'pointer',
                          textDecoration: blocked ? 'line-through' : 'none',
                        }}
                      >
                        {time}
                      </button>
                    );
                  })}
                </div>

                {selectedDate && blockedSlots.some((s) => s.date === getDateKey(selectedDate)) && (
                  <p
                    className="font-mono text-[9px] uppercase tracking-widest mb-4"
                    style={{ color: 'var(--m-muted-4)' }}
                  >
                    Strikethrough times are unavailable
                  </p>
                )}

                <div>
                  <label htmlFor="booking-timezone" style={labelStyle}>
                    <Globe className="inline w-3 h-3 mr-1" style={{ verticalAlign: 'middle' }} />
                    Timezone
                  </label>
                  <select
                    id="booking-timezone"
                    style={{ ...inputStyle, appearance: 'none' as const }}
                    value={form.watch('timezone')}
                    onChange={(e) => form.setValue('timezone', e.target.value)}
                  >
                    {timezones.map((tz) => (
                      <option key={`tz-${tz}`} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => setStep(0)}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest transition-opacity hover:opacity-60"
                style={{
                  color: 'var(--m-muted)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!selectedDate || !selectedTime}
                className="inline-flex items-center justify-center gap-2 font-mono uppercase text-[10px] tracking-widest px-6 min-h-[48px] w-full sm:w-auto transition-opacity"
                style={{
                  background: 'var(--m-ink)',
                  color: 'var(--m-bg)',
                  border: 'none',
                  opacity: !selectedDate || !selectedTime ? 0.3 : 1,
                  cursor: !selectedDate || !selectedTime ? 'not-allowed' : 'pointer',
                }}
              >
                Add Your Details <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div>
            <p
              className="font-mono text-[10px] uppercase tracking-widest mb-2"
              style={{ color: 'var(--m-muted)' }}
            >
              Step 03
            </p>
            <h2
              className="font-bold uppercase mb-6"
              style={{
                fontFamily: "'Archivo Narrow', sans-serif",
                fontSize: 20,
                color: 'var(--m-ink)',
                letterSpacing: '0.03em',
              }}
            >
              Tell us about your business
            </h2>

            <form className="space-y-5 mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="booking-name" style={labelStyle}>
                    <User className="inline w-3 h-3 mr-1" style={{ verticalAlign: 'middle' }} />
                    Full Name
                  </label>
                  <input
                    id="booking-name"
                    type="text"
                    autoComplete="name"
                    autoCapitalize="words"
                    style={inputStyle}
                    placeholder="Your full name"
                    {...form.register('name', { required: 'Name is required' })}
                  />
                  {form.formState.errors.name && (
                    <p
                      style={{ fontSize: 11, color: '#c00', fontFamily: 'monospace', marginTop: 4 }}
                    >
                      {form.formState.errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="booking-email" style={labelStyle}>
                    <Mail className="inline w-3 h-3 mr-1" style={{ verticalAlign: 'middle' }} />
                    Email Address
                  </label>
                  <input
                    id="booking-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    style={inputStyle}
                    placeholder="you@company.com"
                    {...form.register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Valid email required',
                      },
                    })}
                  />
                  {form.formState.errors.email && (
                    <p
                      style={{ fontSize: 11, color: '#c00', fontFamily: 'monospace', marginTop: 4 }}
                    >
                      {form.formState.errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="booking-company" style={labelStyle}>
                    <Building2
                      className="inline w-3 h-3 mr-1"
                      style={{ verticalAlign: 'middle' }}
                    />
                    Company Name
                  </label>
                  <input
                    id="booking-company"
                    type="text"
                    autoComplete="organization"
                    autoCapitalize="words"
                    style={inputStyle}
                    placeholder="Your company"
                    {...form.register('company', { required: 'Company is required' })}
                  />
                  {form.formState.errors.company && (
                    <p
                      style={{ fontSize: 11, color: '#c00', fontFamily: 'monospace', marginTop: 4 }}
                    >
                      {form.formState.errors.company.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="booking-industry" style={labelStyle}>
                    Industry
                  </label>
                  <select
                    style={{ ...inputStyle, appearance: 'none' as const }}
                    id="booking-industry"
                    {...form.register('industry', { required: true })}
                  >
                    <option value="">Select industry</option>
                    {[
                      'Hospitality',
                      'E-commerce',
                      'Professional Services',
                      'Manufacturing',
                      'Technology',
                      'Other',
                    ].map((i) => (
                      <option key={`book-ind-${i}`} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="booking-stage" style={labelStyle}>
                    Business Stage
                  </label>
                  <select
                    style={{ ...inputStyle, appearance: 'none' as const }}
                    id="booking-stage"
                    {...form.register('stage', { required: true })}
                  >
                    <option value="">Select stage</option>
                    {['Idea / Pre-launch', 'Early stage', 'Growth stage', 'Scaling'].map((s) => (
                      <option key={`book-stage-${s}`} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="booking-challenge" style={labelStyle}>
                    Main Challenge
                  </label>
                  <select
                    style={{ ...inputStyle, appearance: 'none' as const }}
                    id="booking-challenge"
                    {...form.register('challenge', { required: true })}
                  >
                    <option value="">Select challenge</option>
                    {[
                      'Growth strategy',
                      'Lead generation',
                      'Marketing effectiveness',
                      'Brand positioning',
                      'Operational scaling',
                      'Other',
                    ].map((c) => (
                      <option key={`book-chal-${c}`} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="booking-message" style={labelStyle}>
                  <MessageSquare
                    className="inline w-3 h-3 mr-1"
                    style={{ verticalAlign: 'middle' }}
                  />
                  What would you like to cover?
                </label>
                <p
                  style={{
                    fontSize: 11,
                    color: 'var(--m-muted-3)',
                    fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                    marginBottom: 6,
                  }}
                >
                  The more context you give, the more focused and useful the session will be.
                </p>
                <textarea
                  style={{ ...inputStyle, resize: 'none' }}
                  rows={4}
                  placeholder="Describe your current situation, what you've tried, and what outcome you're hoping for..."
                  id="booking-message"
                  {...form.register('message', {
                    required: 'Please provide some context for your consultation',
                  })}
                />
                {form.formState.errors.message && (
                  <p style={{ fontSize: 11, color: '#c00', fontFamily: 'monospace', marginTop: 4 }}>
                    {form.formState.errors.message.message}
                  </p>
                )}
              </div>
            </form>

            <div className="flex items-center justify-between">
              <button
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest transition-opacity hover:opacity-60"
                style={{
                  color: 'var(--m-muted)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
              <button
                onClick={async () => {
                  const valid = await form.trigger([
                    'name',
                    'email',
                    'company',
                    'industry',
                    'stage',
                    'challenge',
                    'message',
                  ]);
                  if (valid) setStep(3);
                }}
                className="inline-flex items-center gap-2 font-mono uppercase text-[10px] tracking-widest px-6 py-3 transition-opacity hover:opacity-80"
                style={{
                  background: 'var(--m-ink)',
                  color: 'var(--m-bg)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Review & Confirm <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div>
            <p
              className="font-mono text-[10px] uppercase tracking-widest mb-2"
              style={{ color: 'var(--m-muted)' }}
            >
              Step 04
            </p>
            <h2
              className="font-bold uppercase mb-6"
              style={{
                fontFamily: "'Archivo Narrow', sans-serif",
                fontSize: 20,
                color: 'var(--m-ink)',
                letterSpacing: '0.03em',
              }}
            >
              Confirm your booking
            </h2>

            <div className="space-y-4 mb-8">
              <div style={{ border: '1px solid rgba(var(--m-ink-rgb),0.1)', padding: 20 }}>
                <p
                  className="font-mono text-[9px] uppercase tracking-widest mb-4"
                  style={{ color: 'var(--m-muted-3)' }}
                >
                  Consultation Details
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    {
                      label: 'Type',
                      value: selectedType?.title,
                      icon: <Briefcase className="w-3 h-3" style={{ color: 'var(--m-muted)' }} />,
                    },
                    {
                      label: 'Duration',
                      value: selectedType?.duration,
                      icon: <Clock className="w-3 h-3" style={{ color: 'var(--m-muted)' }} />,
                    },
                    {
                      label: 'Date',
                      value: `${monthNames[calMonth]} ${selectedDate}, 2026`,
                      icon: <Calendar className="w-3 h-3" style={{ color: 'var(--m-muted)' }} />,
                    },
                    {
                      label: 'Time',
                      value: `${selectedTime} — ${form.getValues('timezone')}`,
                      icon: null,
                    },
                  ].map((item) => (
                    <div key={item.label}>
                      <p
                        className="font-mono text-[9px] uppercase tracking-widest mb-1"
                        style={{ color: 'var(--m-muted-3)' }}
                      >
                        {item.label}
                      </p>
                      <p
                        style={{
                          fontSize: 12,
                          color: 'var(--m-ink)',
                          fontFamily:
                            "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                          fontWeight: 600,
                        }}
                      >
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ border: '1px solid rgba(var(--m-ink-rgb),0.1)', padding: 20 }}>
                <p
                  className="font-mono text-[9px] uppercase tracking-widest mb-4"
                  style={{ color: 'var(--m-muted-3)' }}
                >
                  Your Details
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Name', value: form.getValues('name') },
                    { label: 'Email', value: form.getValues('email') },
                    { label: 'Company', value: form.getValues('company') },
                    { label: 'Industry', value: form.getValues('industry') },
                    { label: 'Stage', value: form.getValues('stage') },
                    { label: 'Challenge', value: form.getValues('challenge') },
                  ].map((item) => (
                    <div key={`confirm-${item.label}`}>
                      <p
                        className="font-mono text-[9px] uppercase tracking-widest mb-0.5"
                        style={{ color: 'var(--m-muted-3)' }}
                      >
                        {item.label}
                      </p>
                      <p
                        style={{
                          fontSize: 12,
                          color: 'var(--m-ink-3)',
                          fontFamily:
                            "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                        }}
                      >
                        {item.value || '—'}
                      </p>
                    </div>
                  ))}
                </div>
                {form.getValues('message') && (
                  <div
                    className="mt-3 pt-3"
                    style={{ borderTop: '1px solid rgba(var(--m-ink-rgb),0.08)' }}
                  >
                    <p
                      className="font-mono text-[9px] uppercase tracking-widest mb-1"
                      style={{ color: 'var(--m-muted-3)' }}
                    >
                      Context provided
                    </p>
                    <p
                      style={{
                        fontSize: 12,
                        color: 'var(--m-ink-4)',
                        fontFamily:
                          "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                        lineHeight: 1.6,
                      }}
                    >
                      {form.getValues('message')}
                    </p>
                  </div>
                )}
              </div>

              <div
                style={{
                  padding: '12px 16px',
                  background: 'rgba(var(--m-ink-rgb),0.03)',
                  border: '1px solid rgba(var(--m-ink-rgb),0.08)',
                }}
              >
                <p
                  style={{
                    fontSize: 11,
                    color: 'var(--m-ink-5)',
                    fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  Confirming this booking reserves the selected date and time immediately. You can
                  change availability later from the admin panel.
                </p>
              </div>
              {submitError && (
                <div
                  role="alert"
                  className="mt-3"
                  style={{
                    padding: '12px 16px',
                    border: '1px solid rgba(220,38,38,0.25)',
                    background: 'rgba(220,38,38,0.06)',
                    color: '#991b1b',
                    fontSize: 12,
                  }}
                >
                  {submitError}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest transition-opacity hover:opacity-60"
                style={{
                  color: 'var(--m-muted)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Edit Details
              </button>
              <button
                onClick={form.handleSubmit(handleSubmit)}
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 font-mono uppercase text-[10px] tracking-widest px-8 py-3 transition-opacity hover:opacity-80"
                style={{
                  background: 'var(--m-ink)',
                  color: 'var(--m-bg)',
                  border: 'none',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-3.5 h-3.5 rounded-full border border-white/30 border-t-white animate-spin" />
                    Confirming...
                  </>
                ) : (
                  <>
                    <CalendarCheck className="w-3.5 h-3.5" />[ Confirm Booking ]
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
